import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';
import { generateGradient } from '../../utils/colorUtils';
import { Icon } from './Icon';
import { iconMappings, IconName } from '../../utils/iconMappings';

const Container = styled.div`
  text-align: center;
  padding: ${spacing[16]} ${spacing[5]};
  position: relative;
`;

const IconWrapper = styled.div`
  margin-bottom: ${spacing[6]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const IconBackground = styled.div<{ gradient: string }>`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${props => props.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: ${props => props.theme.shadows?.lg || shadows.lg};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    animation: rotate 20s linear infinite;
  }

  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const IconInner = styled.div`
  position: relative;
  z-index: 1;
  color: white;
`;

const Title = styled.h3`
  color: ${props => props.theme.colors.text};
  margin: 0 0 ${spacing[4]} 0;
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.semibold};
  font-family: ${typography.fontFamily.display};
`;

const Text = styled.p`
  color: ${props => props.theme.colors.textMuted};
  margin: 0 0 ${spacing[6]} 0;
  font-size: ${typography.fontSize.base};
  font-family: ${typography.fontFamily.primary};
  line-height: ${typography.lineHeight.relaxed};
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const Actions = styled.div`
  display: flex;
  gap: ${spacing[3]};
  justify-content: center;
  flex-wrap: wrap;
`;

export type EmptyStateIconType = 
  | 'inbox' 
  | 'search' 
  | 'folder' 
  | 'userGroup' 
  | 'building' 
  | 'document'
  | 'documentText'
  | 'clock'
  | 'check'
  | IconName;

interface EmptyStateProps {
  icon?: EmptyStateIconType;
  title: string;
  text?: string;
  children?: React.ReactNode;
  gradient?: string;
}

const getDefaultIcon = (iconType?: EmptyStateIconType): IconName => {
  if (!iconType) return 'inbox';
  if (iconType === 'clock') return 'pending';
  if (iconType === 'check') return 'success';
  return iconType as IconName;
};

const getGradientForIcon = (iconType: EmptyStateIconType, theme: any): string => {
  const gradients: Record<string, string> = {
    inbox: generateGradient(theme?.colors?.primary || '#6366f1', theme?.colors?.secondary || '#8b5cf6'),
    search: generateGradient('#f59e0b', '#f97316'),
    folder: generateGradient('#10b981', '#059669'),
    userGroup: generateGradient('#3b82f6', '#2563eb'),
    building: generateGradient('#8b5cf6', '#7c3aed'),
    document: generateGradient('#ec4899', '#db2777'),
    documentText: generateGradient('#06b6d4', '#0891b2'),
    clock: generateGradient('#f59e0b', '#f97316'),
    check: generateGradient('#10b981', '#059669'),
  };
  
  return gradients[iconType] || gradients.inbox;
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon = 'inbox',
  title,
  text,
  children,
  gradient,
}) => {
  const theme = useTheme();
  const iconName = getDefaultIcon(icon);
  const IconComponent = iconMappings[iconName];
  const iconGradient = gradient || getGradientForIcon(icon, theme);

  return (
    <Container>
      <IconWrapper>
        <IconBackground gradient={iconGradient}>
          <IconInner>
            <Icon
              icon={IconComponent}
              size="2xl"
              color="text"
              background={false}
            />
          </IconInner>
        </IconBackground>
      </IconWrapper>
      <Title>{title}</Title>
      {text && <Text>{text}</Text>}
      {children && <Actions>{children}</Actions>}
    </Container>
  );
};

export default EmptyState;
